import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { getStartRoute } from "../../config/locationRegistry";
import { useEffect, useState } from "react";
import GlassCard from "../../interface/GlassCard";


const VIDEO_LENGTH = 37.2; // full hero video length in seconds
const CARD_DELAY_MS = 2000; // card rolls in after the fly-into-logo opener
const COUNTDOWN_MS = (VIDEO_LENGTH - CARD_DELAY_MS / 1000) * 1000; // 34s, ends as video finishes
const TICK_MS = 250;
const START_ROUTE = getStartRoute();

/* ── styled-components ─────────────────────────────────────────────── */

const PageContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 6vh;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: var(--color-grey-0, #18212f);
  font-family: "Inter", "Segoe UI", sans-serif;
`;

const Video = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.5;
  filter: saturate(0.72) brightness(0.92);
`;

const overlayIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(24, 33, 47, 0.62) 0%,
    rgba(24, 33, 47, 0.32) 45%,
    rgba(24, 33, 47, 0.82) 100%
  );
  animation: ${overlayIn} 1s 1s both ease-out;
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse 70% 60% at center,
    transparent 30%,
    rgba(13, 21, 32, 0.55) 100%
  );
  animation: ${overlayIn} 1s 1s both ease-out;
`;

const CardSlot = styled.div`
  margin-bottom: 5%;
`;

const AppName = styled.p`
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-witron-blue, #0e64cb);
`;

const Subtitle = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-witron-grey, #939598);
`;

const Location = styled.p`
  margin: 0 0 24px;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: rgba(147, 149, 152, 0.5);
`;

const Hr = styled.div`
  height: 1px;
  margin-bottom: 24px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
`;

const BarTrack = styled.div`
  height: 3px;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 10px;
  background: rgba(0, 0, 0, 0.4);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: var(--color-witron-blue, #0e64cb);
  box-shadow: 0 0 6px rgba(14, 100, 203, 0.6);
  transition: width ${TICK_MS}ms linear;
`;

const TimerLabel = styled.p`
  margin: 0 0 24px;
  font-size: 11px;
  letter-spacing: 0.05em;
  color: rgba(147, 149, 152, 0.55);
`;

const EnterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 26px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.05em;
  cursor: pointer;
  color: var(--color-blue-700, #e0f2fe);
  border: 1px solid rgba(14, 100, 203, 0.35);
  background: linear-gradient(
    145deg,
    rgba(14, 100, 203, 0.16),
    rgba(14, 100, 203, 0.07)
  );
  box-shadow:
    3px 3px 8px rgba(0, 0, 0, 0.35),
    -1px -1px 4px rgba(255, 255, 255, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(14, 100, 203, 0.6);
    background: linear-gradient(
      145deg,
      rgba(14, 100, 203, 0.28),
      rgba(14, 100, 203, 0.14)
    );
    box-shadow:
      4px 4px 12px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 0 16px rgba(14, 100, 203, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow:
      1px 1px 4px rgba(0, 0, 0, 0.3),
      inset 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const Arrow = styled.span`
  opacity: 0.65;
`;

const Watermark = styled.p`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  z-index: 10;
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.07em;
  color: rgba(147, 149, 152, 0.28);
`;

const LogoStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
`;

const LogoRema = styled.img`
  height: 32px;
  object-fit: contain;
`;

const LogoWitron = styled.img`
  height: 40px;
  opacity: 0.85;
  object-fit: contain;
`;

const LogoDivider = styled.div`
  width: 60%;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.25),
    transparent
  );
`;

// TODO: both branches return the same location — remardo needs its own label.
function getSiteConfig() {
  const host = window.location.hostname;
  if (host === "remardo.rema.no") {
    return { siteId: "remardo", location: "Rema Distribusjon Øst · Vinterbro" };
  }
  // vinterbro.wioss.no, localhost, and anything else
  return { siteId: "vinterbro", location: "Rema Distribusjon Øst · Vinterbro" };
}

function HomePortrait() {
  const navigate = useNavigate();
  const site = getSiteConfig();

  const [running, setRunning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(COUNTDOWN_MS);

  // Start counting once the card has rolled in.
  useEffect(() => {
    const t = setTimeout(() => setRunning(true), CARD_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Deadline-based: survives throttled timers when the PWA is backgrounded.
  useEffect(() => {
    if (!running) return;
    const deadline = Date.now() + COUNTDOWN_MS;
    const id = setInterval(
      () => setRemainingMs(Math.max(0, deadline - Date.now())),
      TICK_MS
    );
    return () => clearInterval(id);
  }, [running]);

  // Navigation is a side effect — it lives in an effect, not a setState updater.
  useEffect(() => {
    if (running && remainingMs === 0) {
      navigate(START_ROUTE, { state: { fromIntro: true } });
    }
  }, [running, remainingMs, navigate]);

  const seconds = Math.ceil(remainingMs / 1000);
  const progress = (1 - remainingMs / COUNTDOWN_MS) * 100;

  const handleEnter = () => navigate(START_ROUTE, { state: { fromIntro: true } });

  return (
    <PageContainer>
      <Video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        src="images/heroMobile.mp4"
        poster="images/building.png"
      />
      <Overlay />
      <Vignette />

      <CardSlot>
        <GlassCard enterDelay={CARD_DELAY_MS} duration={600}>
          <AppName>WiMule</AppName>

          <LogoStack>
            <LogoRema src="images/RemaLogo.svg" alt="REMA 1000" />
            <LogoDivider />
            <LogoWitron src="images/WitronLogo.svg" alt="Witron" />
          </LogoStack>

          <Subtitle>Maintenance Operations Platform</Subtitle>
          <Location>{site.location}</Location>

          <Hr />

          <BarTrack>
            <BarFill style={{ width: `${progress}%` }} />
          </BarTrack>
          <TimerLabel aria-live="off">
            {seconds > 0 ? `Entering WiMule in ${seconds}s` : "Loading…"}
          </TimerLabel>

          <EnterButton onClick={handleEnter}>
            Enter WiMule <Arrow>→</Arrow>
          </EnterButton>
        </GlassCard>
      </CardSlot>

      <Watermark>
        Powered by Witron Global Logistics · {new Date().getFullYear()}
      </Watermark>
    </PageContainer>
  );
}

export default HomePortrait;
